import { Point, Room } from '@/types/floorPlanViewer';
import { logger } from '@/lib/logger';

/**
 * Check if a point is inside a polygon using ray casting algorithm
 */
export function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Find which room contains a given point
 * @param point The point to check
 * @param rooms Array of rooms to check against
 * @returns The room containing the point, or null if no room contains it
 */
export function findRoomContainingPoint(point: Point, rooms: Room[]): Room | null {
  for (const room of rooms) {
    // Parse points if they're strings
    let roomPoints = room.points;
    if (typeof roomPoints === 'string') {
      try {
        roomPoints = JSON.parse(roomPoints);
      } catch (e) {
        logger.error('Failed to parse room points:', e);
        continue;
      }
    }

    if (Array.isArray(roomPoints) && isPointInPolygon(point, roomPoints)) {
      return room;
    }
  }

  return null;
}

/**
 * Get room name or ID for a given point
 * Only considers rooms that meet the confidence threshold
 * @param point The point to check
 * @param rooms Array of rooms to check against
 * @param confidenceThreshold Minimum confidence score (0-1) for room to be considered. Defaults to 0 (all rooms).
 * @returns The room ID if found, null otherwise
 */
export function getRoomIdForPoint(
  point: Point,
  rooms: Room[],
  confidenceThreshold: number = 0,
): string | null {
  // Filter rooms by confidence threshold (same as RoomLayer rendering logic)
  const visibleRooms = rooms.filter(room => {
    const confidence = room.confidence ?? 1; // AI detected rooms have confidence, manual rooms default to 1
    return confidence >= confidenceThreshold;
  });

  const room = findRoomContainingPoint(point, visibleRooms);
  return room ? room.id : null;
}
